import { RefObject, useEffect } from "react";

import { CharlotteChatIframeEventData } from "../types";

/*
 * Child iframe -> parent event examples:
 * window.parent.postMessage(
 *   { action: "navigate_to_job", payload: { jobId: "a0tcg00000FXTXHAA5", jobName: "Pistoolspuiter" } },
 *   window.location.origin,
 * );
 * window.parent.postMessage(
 *   { action: "minimize_charlotte_chat" },
 *   window.location.origin,
 * );
 */

interface UseCharlotteChatIframeEventsArgs {
  iframeOrigin: string;
  iframeRef: RefObject<HTMLIFrameElement | null>;
  onMinimizeCharlotteChatEvent: () => void;
  onNavigateToJobEvent: (payload: {
    jobId: string;
    jobName: string;
  }) => unknown;
}

export const useCharlotteChatIframeEvents = ({
  iframeOrigin,
  iframeRef,
  onMinimizeCharlotteChatEvent,
  onNavigateToJobEvent,
}: UseCharlotteChatIframeEventsArgs) => {
  useEffect(() => {
    const handleMessage = (
      event: MessageEvent<CharlotteChatIframeEventData>,
    ) => {
      if (event.origin !== iframeOrigin) {
        return;
      }

      if (
        iframeRef.current?.contentWindow &&
        event.source !== iframeRef.current.contentWindow
      ) {
        return;
      }

      if (!event?.data) {
        return;
      }

      if (event.data.action === "minimize_charlotte_chat") {
        onMinimizeCharlotteChatEvent();
      }

      if (event.data.action === "navigate_to_job") {
        onNavigateToJobEvent({
          ...event.data.payload,
          jobId: event.data.payload.jobId.split("_")[0], // "a0tcg00000Ik1bRAAR_DUT" transform to "a0tcg00000Ik1bRAAR"
        });
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [
    iframeOrigin,
    iframeRef,
    onMinimizeCharlotteChatEvent,
    onNavigateToJobEvent,
  ]);
};
